import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import {
  Loader2, CreditCard, Smartphone, Check, Crown, Zap, Building2, Users,
  Calendar, BarChart3, Shield, X, ChevronRight, AlertCircle, CheckCircle, Phone, Settings
} from 'lucide-react';

const SubscriptionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, subscriptionStatus, refreshSubscription } = useAuth();
  const [plans, setPlans] = useState([]);
  const [tenant, setTenant] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState('stripe');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState(false);
  const [mpesaStatus, setMpesaStatus] = useState(null);
  const [pollingSessionId, setPollingSessionId] = useState(null);

  // Check for payment success from Stripe redirect
  const sessionId = searchParams.get('session_id');

  const loadData = useCallback(async () => {
    try {
      const [plansRes, txnRes] = await Promise.all([
        api.get('/payments/public/plans'),
        api.get('/payments/transactions'),
      ]);
      setPlans(plansRes.data);
      setTransactions(txnRes.data);

      // Get tenant info
      const meRes = await api.get('/auth/me');
      if (meRes.data?.tenant_id) {
        // Fetch tenant details (we'll get it from settings)
        const settingsRes = await api.get('/settings');
        setTenant({
          plan: settingsRes.data?.plan || 'starter',
          ...settingsRes.data
        });
      }
    } catch (err) {
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Poll Stripe session status after redirect
  useEffect(() => {
    if (sessionId) {
      const checkStatus = async () => {
        try {
          const res = await api.get(`/payments/checkout/status/${sessionId}`);
          if (res.data.payment_status === 'paid') {
            loadData();
          }
        } catch (err) {
          console.error('Status check error:', err);
        }
      };
      checkStatus();
    }
  }, [sessionId, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Poll M-Pesa status
  useEffect(() => {
    let interval;
    if (mpesaStatus?.transaction_id && mpesaStatus?.status === 'pending') {
      interval = setInterval(async () => {
        try {
          const res = await api.get(`/payments/mpesa/status/${mpesaStatus.transaction_id}`);
          if (res.data.payment_status === 'paid') {
            setMpesaStatus({ ...res.data, status: 'success' });
            loadData();
            clearInterval(interval);
          } else if (res.data.status === 'failed') {
            setMpesaStatus({ ...res.data, status: 'failed' });
            clearInterval(interval);
          }
        } catch (err) {
          console.error('M-Pesa status error:', err);
        }
      }, 3000);
    }
    return () => clearInterval(interval);
  }, [mpesaStatus, loadData]);

  const handleManageSubscription = async () => {
    setProcessing(true);
    try {
      const res = await api.post('/payments/billing-portal', { return_url: window.location.href });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      console.error('Billing portal error:', err);
      alert(err.response?.data?.detail || 'Unable to open billing portal. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const handlePayment = async () => {
    if (!selectedPlan) return;
    setProcessing(true);

    try {
      if (paymentMethod === 'stripe') {
        const res = await api.post('/payments/subscribe/stripe', {
          plan_code: selectedPlan.code,
          origin_url: window.location.origin,
          payment_method: 'stripe',
          billing_cycle: billingCycle,
        });
        if (res.data.url) {
          window.location.href = res.data.url;
        }
      } else if (paymentMethod === 'mpesa') {
        if (!phoneNumber || phoneNumber.length < 9) {
          alert('Please enter a valid phone number');
          setProcessing(false);
          return;
        }
        const res = await api.post('/payments/subscribe/mpesa', {
          plan_code: selectedPlan.code,
          origin_url: window.location.origin,
          payment_method: 'mpesa',
          phone_number: phoneNumber,
          billing_cycle: billingCycle,
        });
        if (res.data.transaction_id) {
          setMpesaStatus({ transaction_id: res.data.transaction_id, status: 'pending' });
          setPaymentDialog(false);
        } else if (res.data.error) {
          alert(res.data.error);
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      alert(err.response?.data?.detail || 'Payment failed. Please try again.');
    } finally {
      setProcessing(false);
    }
  };

  const getPlanIcon = (code) => {
    switch (code) {
      case 'starter': return <Zap className="w-6 h-6" />;
      case 'standard': return <Building2 className="w-6 h-6" />;
      case 'advanced': return <BarChart3 className="w-6 h-6" />;
      case 'enterprise': return <Crown className="w-6 h-6" />;
      default: return <Shield className="w-6 h-6" />;
    }
  };

  const formatPrice = (price, currency) => {
    if (price === 0) return 'Custom';
    return new Intl.NumberFormat('en-KE', { style: 'currency', currency: currency || 'KES' }).format(price);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-10 h-10 text-airbnb-pink animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="subscription-page" className="animate-fade-in max-w-6xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-airbnb-gray">Subscription & Billing</h1>
        <p className="text-airbnb-medium-gray mt-1">Manage your subscription plan and payment history</p>
      </div>

      {/* Subscription Status Banner */}
      {subscriptionStatus && !subscriptionStatus.is_super_admin && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
          subscriptionStatus.needs_payment ? 'bg-red-50 border border-red-200' :
          subscriptionStatus.is_trial ? 'bg-blue-50 border border-blue-200' :
          subscriptionStatus.show_expiry_warning ? 'bg-amber-50 border border-amber-200' :
          'bg-emerald-50 border border-emerald-200'
        }`} data-testid="subscription-status-banner">
          <AlertCircle className={`w-5 h-5 flex-shrink-0 ${
            subscriptionStatus.needs_payment ? 'text-red-600' :
            subscriptionStatus.is_trial ? 'text-blue-600' :
            subscriptionStatus.show_expiry_warning ? 'text-amber-600' :
            'text-emerald-600'
          }`} />
          <span className={`font-medium text-sm ${
            subscriptionStatus.needs_payment ? 'text-red-700' :
            subscriptionStatus.is_trial ? 'text-blue-700' :
            subscriptionStatus.show_expiry_warning ? 'text-amber-700' :
            'text-emerald-700'
          }`}>
            {subscriptionStatus.needs_payment
              ? (subscriptionStatus.trial_expired ? 'Your free trial has expired. Please subscribe to continue.' : 'Your subscription has expired. Please renew.')
              : subscriptionStatus.is_trial
                ? `Free trial — ${subscriptionStatus.days_remaining} day${subscriptionStatus.days_remaining !== 1 ? 's' : ''} remaining`
                : subscriptionStatus.show_expiry_warning
                  ? `Subscription expires in ${subscriptionStatus.days_remaining} day${subscriptionStatus.days_remaining !== 1 ? 's' : ''}`
                  : `Active subscription${subscriptionStatus.days_remaining ? ` — ${subscriptionStatus.days_remaining} days remaining` : ''}`
            }
          </span>
        </div>
      )}

      {/* Payment Success Banner */}
      {sessionId && (
        <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3">
          <CheckCircle className="w-5 h-5 text-emerald-600" />
          <span className="text-emerald-700 font-medium">Payment successful! Your subscription has been activated.</span>
        </div>
      )}

      {/* M-Pesa Status Banner */}
      {mpesaStatus && (
        <div className={`mb-6 p-4 rounded-xl flex items-center gap-3 ${
          mpesaStatus.status === 'pending' ? 'bg-amber-50 border border-amber-200' :
          mpesaStatus.status === 'success' ? 'bg-emerald-50 border border-emerald-200' :
          'bg-red-50 border border-red-200'
        }`}>
          {mpesaStatus.status === 'pending' && (
            <>
              <Loader2 className="w-5 h-5 text-amber-600 animate-spin" />
              <span className="text-amber-700 font-medium">Check your phone for M-Pesa prompt. Waiting for payment confirmation...</span>
            </>
          )}
          {mpesaStatus.status === 'success' && (
            <>
              <CheckCircle className="w-5 h-5 text-emerald-600" />
              <span className="text-emerald-700 font-medium">M-Pesa payment successful! Receipt: {mpesaStatus.mpesa_receipt}</span>
            </>
          )}
          {mpesaStatus.status === 'failed' && (
            <>
              <AlertCircle className="w-5 h-5 text-red-600" />
              <span className="text-red-700 font-medium">M-Pesa payment failed. Please try again.</span>
            </>
          )}
        </div>
      )}

      {/* Current Plan */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6 mb-8">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-airbnb-gray">Current Plan</h2>
          <div className="flex items-center gap-2">
            {transactions.length > 0 && (
              <Button
                onClick={handleManageSubscription}
                variant="outline"
                size="sm"
                disabled={processing}
                className="rounded-xl text-sm"
                data-testid="manage-subscription-btn"
              >
                {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Settings className="w-4 h-4 mr-1.5" />}
                Manage Subscription
              </Button>
            )}
          </div>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-airbnb-light-pink rounded-xl flex items-center justify-center text-airbnb-pink">
              {getPlanIcon(tenant?.plan || 'starter')}
            </div>
            <div>
              <p className="font-bold text-airbnb-gray capitalize">{tenant?.plan || 'Starter'} Plan</p>
              <p className="text-sm text-airbnb-medium-gray">
                {subscriptionStatus?.is_trial ? `Free trial — ${subscriptionStatus.days_remaining ?? 0} days left` :
                 subscriptionStatus?.needs_payment ? 'Expired — please renew' :
                 subscriptionStatus?.billing_end ? `Renews ${new Date(subscriptionStatus.billing_end).toLocaleDateString()}` :
                 'Active subscription'}
                {subscriptionStatus?.billing_cycle === 'annual' && ' (Annual)'}
              </p>
            </div>
          </div>
          <span className={`px-3 py-1 text-sm font-semibold rounded-full ${
            subscriptionStatus?.needs_payment ? 'bg-red-100 text-red-700' :
            subscriptionStatus?.is_trial ? 'bg-blue-100 text-blue-700' :
            'bg-emerald-100 text-emerald-700'
          }`}>
            {subscriptionStatus?.needs_payment ? 'Expired' : subscriptionStatus?.is_trial ? 'Trial' : 'Active'}
          </span>
        </div>
      </div>

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold text-airbnb-gray">Available Plans</h2>
        <div className="flex items-center gap-2 bg-gray-100 rounded-xl p-1" data-testid="billing-cycle-toggle">
          <button
            onClick={() => setBillingCycle('monthly')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all ${
              billingCycle === 'monthly' ? 'bg-white text-airbnb-gray shadow-sm' : 'text-airbnb-medium-gray'
            }`}
            data-testid="billing-monthly"
          >
            Monthly
          </button>
          <button
            onClick={() => setBillingCycle('annual')}
            className={`px-4 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 ${
              billingCycle === 'annual' ? 'bg-white text-airbnb-gray shadow-sm' : 'text-airbnb-medium-gray'
            }`}
            data-testid="billing-annual"
          >
            Annual
            <span className="text-[10px] bg-emerald-100 text-emerald-700 px-1.5 py-0.5 rounded-full font-bold">2 MONTHS FREE</span>
          </button>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {plans.map((plan) => {
          const isCurrentPlan = (tenant?.plan || 'starter') === plan.code;
          const monthlyPrice = plan.price;
          const annualPrice = Math.round(monthlyPrice * 10); // 2 months free
          const displayPrice = billingCycle === 'annual' ? annualPrice : monthlyPrice;
          const monthlyEquivalent = billingCycle === 'annual' ? Math.round(annualPrice / 12) : monthlyPrice;
          return (
            <div
              key={plan.code}
              data-testid={`plan-${plan.code}`}
              className={`bg-white rounded-2xl border p-5 relative transition-all ${
                isCurrentPlan ? 'border-airbnb-pink ring-2 ring-airbnb-pink/20' : 'border-gray-100 hover:border-gray-200'
              }`}
            >
              {isCurrentPlan && (
                <span className="absolute -top-2 left-4 px-2 py-0.5 bg-airbnb-pink text-white text-[10px] font-bold rounded-full">
                  CURRENT
                </span>
              )}
              {billingCycle === 'annual' && plan.price > 0 && (
                <span className="absolute -top-2 right-4 px-2 py-0.5 bg-emerald-500 text-white text-[10px] font-bold rounded-full">
                  SAVE {formatPrice(monthlyPrice * 2, plan.currency)}
                </span>
              )}
              <div className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 ${
                isCurrentPlan ? 'bg-airbnb-pink text-white' : 'bg-gray-100 text-airbnb-gray'
              }`}>
                {getPlanIcon(plan.code)}
              </div>
              <h3 className="font-bold text-airbnb-gray">{plan.name}</h3>
              <div className="mt-2 mb-1">
                <span className="text-2xl font-bold text-airbnb-gray">{formatPrice(displayPrice, plan.currency)}</span>
                {plan.price > 0 && <span className="text-sm text-airbnb-medium-gray">/{billingCycle === 'annual' ? 'year' : 'month'}</span>}
              </div>
              {billingCycle === 'annual' && plan.price > 0 && (
                <p className="text-xs text-airbnb-medium-gray mb-3">
                  {formatPrice(monthlyEquivalent, plan.currency)}/mo &middot; <span className="line-through">{formatPrice(monthlyPrice * 12, plan.currency)}</span>
                </p>
              )}
              {billingCycle === 'monthly' && <div className="mb-3" />}
              <ul className="space-y-2 mb-4">
                <li className="flex items-center gap-2 text-sm text-airbnb-gray">
                  <Building2 className="w-3.5 h-3.5 text-airbnb-teal" />
                  {plan.max_properties === 999 ? 'Unlimited' : plan.max_properties} Properties
                </li>
                <li className="flex items-center gap-2 text-sm text-airbnb-gray">
                  <Users className="w-3.5 h-3.5 text-airbnb-teal" />
                  {plan.max_users === 999 ? 'Unlimited' : plan.max_users} Users
                </li>
                {plan.trial_days > 0 && (
                  <li className="flex items-center gap-2 text-sm text-airbnb-gray">
                    <Calendar className="w-3.5 h-3.5 text-airbnb-teal" />
                    {plan.trial_days}-day free trial
                  </li>
                )}
              </ul>
              <Button
                onClick={() => { setSelectedPlan(plan); setPaymentDialog(true); }}
                disabled={isCurrentPlan && !subscriptionStatus?.needs_payment}
                className={`w-full rounded-xl text-sm ${
                  isCurrentPlan && !subscriptionStatus?.needs_payment
                    ? 'bg-gray-100 text-airbnb-medium-gray cursor-not-allowed'
                    : 'bg-airbnb-pink hover:bg-airbnb-dark-pink text-white'
                }`}
                data-testid={`select-plan-${plan.code}`}
              >
                {isCurrentPlan && !subscriptionStatus?.needs_payment ? 'Current Plan' : plan.is_contact_us ? 'Contact Sales' : subscriptionStatus?.needs_payment ? 'Subscribe' : 'Upgrade'}
                {(!isCurrentPlan || subscriptionStatus?.needs_payment) && !plan.is_contact_us && <ChevronRight className="w-4 h-4 ml-1" />}
              </Button>
            </div>
          );
        })}
      </div>

      {/* Transaction History */}
      <div className="bg-white rounded-2xl border border-gray-100 p-6">
        <h2 className="text-lg font-bold text-airbnb-gray mb-4">Payment History</h2>
        {transactions.length === 0 ? (
          <p className="text-airbnb-medium-gray text-sm py-4 text-center">No payment history yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="text-left text-xs text-airbnb-medium-gray border-b border-gray-100">
                  <th className="pb-3 font-medium">Date</th>
                  <th className="pb-3 font-medium">Plan</th>
                  <th className="pb-3 font-medium">Amount</th>
                  <th className="pb-3 font-medium">Method</th>
                  <th className="pb-3 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map((txn) => (
                  <tr key={txn.transaction_id} className="border-b border-gray-50 last:border-0" data-testid={`txn-${txn.transaction_id}`}>
                    <td className="py-3 text-sm text-airbnb-gray">
                      {new Date(txn.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3 text-sm text-airbnb-gray capitalize">{txn.plan_code}</td>
                    <td className="py-3 text-sm font-medium text-airbnb-gray">
                      {formatPrice(txn.amount, txn.currency)}
                    </td>
                    <td className="py-3">
                      <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                        txn.payment_method === 'stripe' ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {txn.payment_method === 'stripe' ? <CreditCard className="w-3 h-3" /> : <Smartphone className="w-3 h-3" />}
                        {txn.payment_method === 'stripe' ? 'Card' : 'M-Pesa'}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        txn.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                        txn.status === 'failed' ? 'bg-red-100 text-red-700' :
                        'bg-amber-100 text-amber-700'
                      }`}>
                        {txn.payment_status === 'paid' ? 'Paid' : txn.status === 'failed' ? 'Failed' : 'Pending'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Payment Method Dialog */}
      <Dialog open={paymentDialog} onOpenChange={setPaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-airbnb-pink" />
              Complete Payment
            </DialogTitle>
            <DialogDescription className="sr-only">Choose payment method for subscription</DialogDescription>
          </DialogHeader>

          {selectedPlan && (
            <div className="space-y-5">
              {/* Plan Summary */}
              <div className="bg-gray-50 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-airbnb-medium-gray">Plan</span>
                  <span className="font-bold text-airbnb-gray">{selectedPlan.name}</span>
                </div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-airbnb-medium-gray">Billing</span>
                  <span className="font-medium text-airbnb-gray capitalize">{billingCycle}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-airbnb-medium-gray">Amount</span>
                  <div className="text-right">
                    <span className="font-bold text-airbnb-pink text-lg">
                      {formatPrice(billingCycle === 'annual' ? Math.round(selectedPlan.price * 10) : selectedPlan.price, selectedPlan.currency)}
                    </span>
                    <span className="text-xs text-airbnb-medium-gray font-normal">/{billingCycle === 'annual' ? 'year' : 'month'}</span>
                    {billingCycle === 'annual' && (
                      <p className="text-xs text-emerald-600 font-medium">You save {formatPrice(selectedPlan.price * 2, selectedPlan.currency)}!</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div>
                <Label className="text-sm font-medium text-airbnb-gray mb-3 block">Payment Method</Label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setPaymentMethod('stripe')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'stripe' ? 'border-airbnb-pink bg-airbnb-light-pink/30' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="payment-stripe"
                  >
                    <CreditCard className={`w-6 h-6 mx-auto mb-2 ${paymentMethod === 'stripe' ? 'text-airbnb-pink' : 'text-airbnb-gray'}`} />
                    <p className={`text-sm font-medium ${paymentMethod === 'stripe' ? 'text-airbnb-pink' : 'text-airbnb-gray'}`}>Card</p>
                    <p className="text-xs text-airbnb-medium-gray">Visa, Mastercard</p>
                  </button>
                  <button
                    onClick={() => setPaymentMethod('mpesa')}
                    className={`p-4 rounded-xl border-2 transition-all ${
                      paymentMethod === 'mpesa' ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                    }`}
                    data-testid="payment-mpesa"
                  >
                    <Smartphone className={`w-6 h-6 mx-auto mb-2 ${paymentMethod === 'mpesa' ? 'text-emerald-600' : 'text-airbnb-gray'}`} />
                    <p className={`text-sm font-medium ${paymentMethod === 'mpesa' ? 'text-emerald-600' : 'text-airbnb-gray'}`}>M-Pesa</p>
                    <p className="text-xs text-airbnb-medium-gray">Mobile Money</p>
                  </button>
                </div>
              </div>

              {/* M-Pesa Phone Input */}
              {paymentMethod === 'mpesa' && (
                <div>
                  <Label className="text-sm font-medium text-airbnb-gray mb-1.5 block">M-Pesa Phone Number</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-airbnb-medium-gray" />
                    <Input
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      placeholder="0712345678 or 254712345678"
                      className="pl-10"
                      data-testid="mpesa-phone-input"
                    />
                  </div>
                  <p className="text-xs text-airbnb-medium-gray mt-1.5">You will receive an STK push prompt on this number</p>
                </div>
              )}

              {/* Submit Button */}
              <Button
                onClick={handlePayment}
                disabled={processing || (paymentMethod === 'mpesa' && !phoneNumber)}
                className="w-full bg-airbnb-pink hover:bg-airbnb-dark-pink text-white rounded-xl py-3"
                data-testid="submit-payment-btn"
              >
                {processing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    {paymentMethod === 'stripe' ? 'Pay with Card' : 'Pay with M-Pesa'}
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>

              <p className="text-xs text-center text-airbnb-medium-gray">
                Secure payment processed by {paymentMethod === 'stripe' ? 'Stripe' : 'Safaricom M-Pesa'}
              </p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SubscriptionPage;
